import { z } from 'zod';

/*
 * Zod-validated import.meta.env — fail fast at boot (spec §3.9, §4.1).
 * Telemetry vars are optional; when unset, telemetry is disabled so local dev
 * and tests emit nothing (spec §4.5).
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.string().default('/api'),
  VITE_ENABLE_MOCK: z
    .string()
    .optional()
    .transform((v) => v === 'true'),
  /*
   * Identity's SESSION_COOKIE_SUFFIX for this environment ("dev", "sbx").
   * Identity writes csrf_token__<suffix> when set and reads that name first,
   * then the plain csrf_token; the BFF reads the same way. Empty locally.
   */
  VITE_SESSION_COOKIE_SUFFIX: z.string().optional(),
  /*
   * Where sign-in starts. Sign-in is browser -> Identity's hosted login
   * (architecture "Who establishes the caller"); the BFF's /sign-in route
   * redirects there with the post-login return URL. Default: <API base>/sign-in.
   */
  VITE_SIGN_IN_URL: z.string().optional(),
  VITE_RUM_APP_ID: z.string().optional(),
  VITE_RUM_REGION: z.string().optional(),
  VITE_RUM_IDENTITY_POOL: z.string().optional(),
  VITE_RUM_SAMPLE_RATE: z
    .string()
    .optional()
    .transform((v) => (v === undefined ? 1 : Number(v)))
    .pipe(z.number().min(0).max(1)),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  // Fail fast: a misconfigured environment must not boot silently.
  console.error('Invalid environment configuration', parsed.error.flatten());
  throw new Error('Invalid environment configuration');
}

export const env = {
  API_BASE_URL: parsed.data.VITE_API_BASE_URL,
  ENABLE_MOCK: parsed.data.VITE_ENABLE_MOCK ?? false,
  SESSION_COOKIE_SUFFIX: (parsed.data.VITE_SESSION_COOKIE_SUFFIX ?? '').trim(),
  SIGN_IN_URL:
    parsed.data.VITE_SIGN_IN_URL ??
    `${parsed.data.VITE_API_BASE_URL.replace(/\/$/, '')}/sign-in`,
  RUM_APP_ID: parsed.data.VITE_RUM_APP_ID,
  RUM_REGION: parsed.data.VITE_RUM_REGION,
  RUM_IDENTITY_POOL: parsed.data.VITE_RUM_IDENTITY_POOL,
  RUM_SAMPLE_RATE: parsed.data.VITE_RUM_SAMPLE_RATE,
} as const;
